var _a;
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useMemo } from "react";
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, } from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { formatPercentOne, isNumber } from "@sequentech/ui-core";
import { TALLY_RESULTS_PIE_HEIGHT, TALLY_RESULTS_PIE_PANEL_WIDTH, withEscapedChartText, } from "@sequentech/ui-essentials";
import { useTranslation } from "react-i18next";
import { translatedLabel } from "@/services/resultLabels";
import { entityClassName } from "@/services/cssClassNames";
var percent = function (value) { return (isNumber(value) ? formatPercentOne(value) : "-"); };
var valueOrDash = function (value) {
    return typeof value === "string" || typeof value === "number" ? value : "-";
};
var stringOrUndefined = function (value) {
    return typeof value === "string" && value.length > 0 ? value : undefined;
};
var sameId = function (left, right) {
    return left !== null &&
        left !== undefined &&
        right !== null &&
        right !== undefined &&
        String(left) === String(right);
};
// Apex places its scrollable legend inside an SVG foreignObject; make that
// region reachable by keyboard after each render so users can scroll its labels.
var focusableLegend = function (_a) {
    var el = _a.el;
    var legend = el.querySelector(".apexcharts-legend");
    if (legend)
        legend.tabIndex = 0;
};
var Chart = ((_a = ReactApexChart
    .default) !== null && _a !== void 0 ? _a : ReactApexChart);
var finiteNumber = function (value) {
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === "string" && value.trim().length > 0) {
        var parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};
var GeneralInformationChart = function (_a) {
    var electionName = _a.electionName, result = _a.result;
    var t = useTranslation().t;
    var chartData = useMemo(function () {
        var eligibleCensus = finiteNumber(result.elegible_census);
        var totalVoters = finiteNumber(result.total_voters);
        if (eligibleCensus === null || totalVoters === null) {
            return [];
        }
        var representedData = [
            {
                label: t("resultsPortal.summary.totalVotesCounted"),
                value: totalVoters,
            },
            {
                label: t("resultsPortal.resultsAndParticipation.nonVoters"),
                value: Math.max(eligibleCensus - totalVoters, 0),
            },
        ].filter(function (item) { return item.value > 0; });
        return representedData.length > 0
            ? representedData
            : [
                {
                    label: t("resultsPortal.resultsAndParticipation.nonVoters"),
                    value: 100,
                },
            ];
    }, [result.elegible_census, result.total_voters, t]);
    var chartOptions = useMemo(function () { return ({
        options: withEscapedChartText({
            chart: { events: { mounted: focusableLegend, updated: focusableLegend } },
            labels: chartData.map(function (item) { return item.label; }),
            legend: { position: "right" },
            responsive: [
                {
                    breakpoint: 480,
                    options: {
                        chart: { width: 200 },
                        legend: { position: "bottom" },
                    },
                },
            ],
        }),
        series: chartData.map(function (item) { return item.value; }),
    }); }, [chartData]);
    if (chartData.length === 0) {
        return null;
    }
    return (_jsxs(Paper, { className: [
            "seq-results-summary__chart",
            entityClassName("summary-election", String(result.election_id)),
            result.area_id
                ? entityClassName("summary-area", String(result.area_id))
                : "seq-results-summary-area--global",
        ].join(" "), "data-election-id": String(result.election_id), variant: "outlined", sx: { p: 2, width: "100%", maxWidth: TALLY_RESULTS_PIE_PANEL_WIDTH }, children: [_jsx(Typography, { className: "seq-results-summary__chart-title", variant: "body1", sx: { color: "grey.600", fontSize: "16px", fontWeight: 400 }, children: electionName }), _jsx(Box, { className: "seq-results-summary__chart-body", sx: { borderTop: "1px solid", borderColor: "divider", mt: 2, pt: 2 }, children: _jsx(Chart, { className: "seq-results-summary__pie", options: chartOptions.options, series: chartOptions.series, type: "pie", width: "100%", height: TALLY_RESULTS_PIE_HEIGHT }) })] }));
};
export var ResultsSummary = function (_a) {
    var elections = _a.elections, resultsElections = _a.resultsElections, locale = _a.locale;
    var t = useTranslation().t;
    var showBlankBallotsColumn = useMemo(function () { return resultsElections.some(function (row) { return isNumber(row.blank_ballots); }); }, [resultsElections]);
    return (_jsxs(Box, { className: "seq-results-summary", component: "section", sx: { mt: { xs: 3, md: 5 } }, children: [_jsx(Typography, { className: "seq-results-summary__title", component: "h2", variant: "h5", sx: { mb: 2 }, children: t("resultsPortal.summary.title") }), _jsxs(Box, { className: "seq-results-summary__content", sx: {
                    display: "flex",
                    flexDirection: { xs: "column", lg: "row" },
                    gap: 4,
                    alignItems: "flex-start",
                }, children: [_jsx(Box, { className: "seq-results-summary__charts", sx: {
                            display: "flex",
                            flex: {
                                xs: "1 1 auto",
                                lg: "0 0 ".concat(TALLY_RESULTS_PIE_PANEL_WIDTH, "px"),
                            },
                            flexDirection: "column",
                            gap: 2,
                            width: { xs: "100%", lg: TALLY_RESULTS_PIE_PANEL_WIDTH },
                            maxWidth: "100%",
                        }, children: resultsElections.map(function (result) {
                            var _a;
                            var election = elections.find(function (row) { return sameId(row.id, result.election_id); });
                            var electionName = (_a = stringOrUndefined(result.name)) !== null && _a !== void 0 ? _a : translatedLabel(election, locale, t("resultsPortal.summary.election"));
                            return (_jsx(GeneralInformationChart, { electionName: electionName, result: result }, "".concat(result.election_id, "-").concat(result.id)));
                        }) }), _jsx(Box, { className: "seq-results-summary__table-column", sx: { flex: "1 1 auto", minWidth: 0, width: "100%" }, children: _jsx(TableContainer, { className: "seq-results-summary__table-container", sx: {
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                overflowX: "auto",
                                bgcolor: "background.paper",
                            }, children: _jsxs(Table, { className: "seq-results-summary__table", "aria-label": t("resultsPortal.summary.ariaLabel"), sx: { minWidth: 680 }, children: [_jsx(TableHead, { className: "seq-results-summary__table-head", children: _jsxs(TableRow, { className: "seq-results-summary__heading-row", children: [_jsx(TableCell, { className: "seq-results-summary__election-heading", children: t("resultsPortal.summary.election") }), _jsx(TableCell, { className: "seq-results-summary__eligible-heading", align: "right", children: t("resultsPortal.summary.eligibleVoters") }), _jsx(TableCell, { className: "seq-results-summary__counted-heading", align: "right", children: t("resultsPortal.summary.totalVotesCounted") }), showBlankBallotsColumn && (_jsx(TableCell, { className: "seq-results-summary__blank-ballots-heading", align: "right", children: t("resultsPortal.summary.totalBlankBallots") })), _jsx(TableCell, { className: "seq-results-summary__participation-heading", align: "right", children: t("resultsPortal.summary.participation") })] }) }), _jsx(TableBody, { className: "seq-results-summary__table-body", children: resultsElections.map(function (result) {
                                            var _a;
                                            var election = elections.find(function (row) {
                                                return sameId(row.id, result.election_id);
                                            });
                                            return (_jsxs(TableRow, { className: [
                                                    "seq-results-summary__row",
                                                    entityClassName("summary-election-row", String(result.election_id)),
                                                    result.area_id
                                                        ? entityClassName("summary-area-row", String(result.area_id))
                                                        : "seq-results-summary-area-row--global",
                                                ].join(" "), children: [_jsx(TableCell, { className: "seq-results-summary__election-cell", children: (_a = stringOrUndefined(result.name)) !== null && _a !== void 0 ? _a : translatedLabel(election, locale, t("resultsPortal.summary.election")) }), _jsx(TableCell, { className: "seq-results-summary__eligible-cell", align: "right", children: valueOrDash(result.elegible_census) }), _jsx(TableCell, { className: "seq-results-summary__counted-cell", align: "right", children: valueOrDash(result.total_voters) }), showBlankBallotsColumn && (_jsx(TableCell, { className: "seq-results-summary__blank-ballots-cell", align: "right", children: valueOrDash(result.blank_ballots) })), _jsx(TableCell, { className: "seq-results-summary__participation-cell", align: "right", children: percent(result.total_voters_percent) })] }, "".concat(result.election_id, "-").concat(result.id)));
                                        }) })] }) }) })] })] }));
};
